import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

/** Search BGG XML API directly from the browser, bypassing server-side Cloudflare blocking. */
async function searchBGGClientSide(query) {
  const BGG = 'https://boardgamegeek.com/xmlapi2';
  const parser = new DOMParser();

  // Step 1: name search
  const searchRes = await fetch(`${BGG}/search?query=${encodeURIComponent(query)}&type=boardgame`);
  if (!searchRes.ok) throw new Error(`BGG search failed (${searchRes.status})`);
  const searchDoc = parser.parseFromString(await searchRes.text(), 'text/xml');

  const candidates = Array.from(searchDoc.querySelectorAll('item'))
    .map(item => {
      const nameEl = item.querySelector('name[type="primary"]') || item.querySelector('name');
      return {
        bggId: parseInt(item.getAttribute('id'), 10),
        name: nameEl?.getAttribute('value') || '',
        year: parseInt(item.querySelector('yearpublished')?.getAttribute('value'), 10) || null,
      };
    })
    .sort((a, b) => (b.year || 0) - (a.year || 0))
    .slice(0, 20);

  if (candidates.length === 0) return [];

  // Step 2: batch detail fetch for images + metadata
  const ids = candidates.map(c => c.bggId).join(',');
  let detailDoc = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const dRes = await fetch(`${BGG}/thing?id=${ids}&stats=0`);
    if (dRes.status === 202) { await new Promise(r => setTimeout(r, 2000)); continue; }
    if (!dRes.ok) break;
    detailDoc = parser.parseFromString(await dRes.text(), 'text/xml');
    break;
  }

  const detailMap = {};
  if (detailDoc) {
    Array.from(detailDoc.querySelectorAll('item')).forEach(item => {
      const id = parseInt(item.getAttribute('id'), 10);
      const imgText = item.querySelector('image')?.textContent || '';
      const imageUrl = imgText ? `https:${imgText}`.replace('https:https:', 'https:') : '';
      detailMap[id] = {
        imageUrl,
        designers: Array.from(item.querySelectorAll('link[type="boardgamedesigner"]')).map(l => l.getAttribute('value')).join(', '),
        publisher: item.querySelector('link[type="boardgamepublisher"]')?.getAttribute('value') || '',
        category: item.querySelector('link[type="boardgamecategory"]')?.getAttribute('value') || '',
        minPlayers: parseInt(item.querySelector('minplayers')?.getAttribute('value'), 10) || null,
        maxPlayers: parseInt(item.querySelector('maxplayers')?.getAttribute('value'), 10) || null,
        playingTime: parseInt(item.querySelector('playingtime')?.getAttribute('value'), 10) || null,
      };
    });
  }

  return candidates.map(c => ({
    id: c.bggId,
    bggId: c.bggId,
    name: c.name,
    year: c.year,
    imageUrl: detailMap[c.bggId]?.imageUrl || '',
    designers: detailMap[c.bggId]?.designers || '',
    publisher: detailMap[c.bggId]?.publisher || '',
    category: detailMap[c.bggId]?.category || '',
    genre: detailMap[c.bggId]?.category || '',
    minPlayers: detailMap[c.bggId]?.minPlayers || null,
    maxPlayers: detailMap[c.bggId]?.maxPlayers || null,
    playingTime: detailMap[c.bggId]?.playingTime || null,
  }));
}

/**
 * Custom hook to handle search functionality for external APIs
 * @param {string} searchType - Type of search ('games', 'consoles', 'lego')
 * @returns {Object} Search state and functions
 */
const useSearch = (searchType, initialSearchSource = 'musicbrainz') => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedItemPlatforms, setSelectedItemPlatforms] = useState({});
  const [searchSource, setSearchSource] = useState(initialSearchSource);

  // Extract base type from search type (removing '-wishlist' if present)
  const getBaseType = useCallback(() => {
    // Make sure to handle all wishlist types correctly
    if (searchType === 'movie-wishlist') {
      return 'movies';
    }
    return searchType.replace('-wishlist', '');
  }, [searchType]);
  
  // Get the appropriate search endpoint based on search type
  const getSearchEndpoint = useCallback(() => {
    // For all collection types, we need to use the same endpoints
    // regardless of whether it's a collection or wishlist
    
    // Get the base type
    const baseType = getBaseType();
    
    // Map each base type to its correct API endpoint
    switch (baseType) {
      case 'games':
        return '/rawg/search';
      
      case 'consoles':
        return '/consoles/search';
      
      case 'lego':
        return '/lego/search';
      
      case 'movies':
        return '/movies/search';
      
      case 'music':
        return searchSource === 'discogs' ? '/music/search-discogs' : '/music/search';

      case 'books':
      case 'book':
      case 'book-wishlist':
        return '/books/search';

      case 'board-games':
      case 'board-game':
      case 'board-game-wishlist':
        return '/board-games/search';

      case 'trading-cards':
      case 'trading-cards-wishlist':
        return `/trading-cards/search?game=${searchSource}`;

      case 'manga':
      case 'manga-wishlist':
        return '/manga/search';

      case 'comics':
      case 'comics-wishlist':
        return '/comics/search';

      // Default fallback for games
      default:
        return '/rawg/search';
    }
  }, [getBaseType, searchSource]);
  

  // Perform search
  const handleSearch = async (e) => {
    // Handle different types of input parameters
    if (e && typeof e === 'object') {
      // If it's an event with preventDefault, call it
      if (e.preventDefault && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      
      // If it has a query property, use that to update searchQuery
      if (e.query !== undefined) {
        setSearchQuery(e.query);
      }
    } else if (typeof e === 'string') {
      // If it's a direct string, update searchQuery
      setSearchQuery(e);
    }
    
    // Use the current searchQuery state or the one we just set
    const queryToUse = (e && typeof e === 'object' && e.query !== undefined) ? e.query : searchQuery;
    
    if (!queryToUse.trim()) {
      setIsSearching(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setIsSearching(true);
      setError('');
      setSearchPage(1); // Reset page when starting a new search
      
      const baseType = getBaseType();

      const endpoint = getSearchEndpoint();

      // For wishlist searches, we need to add the wishlist parameter
      const isWishlist = searchType.includes('-wishlist');

      // For music searches, use fast mode to avoid slow detail lookups
      const musicFastParams = baseType === 'music' && searchSource !== 'discogs' ? { fast: true, detailedCount: 0 } : {};

      // Discogs uses 'q' param; all others use 'query'
      const isDiscogs = baseType === 'music' && searchSource === 'discogs';
      const queryParamKey = isDiscogs ? 'q' : 'query';

      const platformParam = (e && typeof e === 'object' && e.platform !== undefined) ? e.platform : undefined;
      const response = await axiosInstance.get(endpoint, {
        params: {
          [queryParamKey]: queryToUse,
          ...(isDiscogs ? {} : { page: 1, wishlist: isWishlist }),
          _t: Date.now(),
          ...(platformParam ? { platform: platformParam } : {}),
          ...musicFastParams
        }
      });


      // Process the search results based on the response format
      let results = [];
      let hasMore = false;

      // Handle different response formats
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array format (like Lego API)
          results = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // Nested results format (like RAWG API)
          results = response.data.results;
          hasMore = !!response.data.next;
        } else if (typeof response.data === 'object') {
          // Single object result
          results = [response.data];
        }
      }
      
      // Special handling for different search types
      // baseType is already computed above
      
      if (baseType === 'games') {
        // For games, ensure we have proper image URLs
        results = results.map(item => ({
          ...item,
          imageUrl: item.background_image || item.imageUrl || ''
        }));
      } else if (baseType === 'consoles') {
        // For consoles, don't set imageUrl - let backend fetch Wikipedia hardware images
        results = results.map(item => ({
          ...item
          // imageUrl intentionally not set - backend will fetch Wikipedia hardware image
        }));
      } else if (baseType === 'lego') {
        // For Lego, ensure we have proper image URLs
        results = results.map(item => ({
          ...item,
          imageUrl: item.set_img_url || item.imageUrl || ''
        }));
      } else if (baseType === 'movies') {
        // For movies, transform OMDB API results to match our format
        results = results.map(item => ({
          ...item,
          name: item.Title || item.name,
          year: item.Year || item.year,
          director: item.Director || item.director,
          genre: item.Genre || item.genre,
          imageUrl: item.Poster !== 'N/A' ? item.Poster : (item.imageUrl || ''),
          format: item.format || 'Not Specified',
          condition: item.condition || 'Not Specified',
          imdbID: item.imdbID || item.id
        }));
      } else if (baseType === 'books' || baseType === 'book') {
        // For books, ensure proper data format from Open Library response
        results = results.map(item => ({
          ...item,
          name: item.name || item.title || '',
          author: item.author || '',
          year: item.year || '',
          genre: item.genre || '',
          imageUrl: item.imageUrl || ''
        }));
      } else if (baseType === 'board-games' || baseType === 'board-game') {
        results = results.map(item => ({
          ...item,
          name: item.name || '',
          designers: item.designers || '',
          publisher: item.publisher || '',
          year: item.year || '',
          minPlayers: item.minPlayers || null,
          maxPlayers: item.maxPlayers || null,
          playingTime: item.playingTime || null,
          category: item.category || '',
          genre: item.genre || item.category || '',
          imageUrl: item.imageUrl || '',
          bggId: item.bggId || item.id || null
        }));
      } else if (baseType === 'trading-cards') {
        // Results are already normalised by the backend — pass through as-is
        results = results.map(item => ({ ...item }));
      } else if (baseType === 'manga') {
        // Results are already normalised by the backend — pass through as-is
        results = results.map(item => ({ ...item }));
      } else if (baseType === 'comics') {
        // Results are already normalised by the backend — pass through as-is
        results = results.map(item => ({ ...item }));
      } else if (baseType === 'music') {
        if (searchSource === 'discogs') {
          // Discogs results are already normalised by our backend
          results = results.map(item => ({
            id: item.discogsId || item.id,
            discogsId: item.discogsId || null,
            discogsUrl: item.discogsUrl || null,
            name: item.name || item.title,
            artist: item.artist,
            year: item.year,
            label: item.label,
            genre: item.genre || null,
            format: 'Vinyl',
            pressing: item.pressing || null,
            catalogueNumber: item.catalogueNumber || null,
            country: item.country || null,
            imageUrl: item.imageUrl || '',
          }));
        } else {
          // MusicBrainz results
          results = results.map(item => {
            const safeFormat = item.format && ['Vinyl','CD','Cassette','8-Track','Digital','Box Set','Other'].includes(item.format) ? item.format : 'CD';
            const safeCondition = item.condition && ['Mint (M)','Near Mint (NM)','Very Good Plus (VG+)','Very Good (VG)','Good (G)','Poor (P)','Factory Sealed','New','Like New','Good / Acceptable','For Parts / Not Working'].includes(item.condition) ? item.condition : 'Very Good (VG)';
            const safeEdition = item.edition && ['Standard Release','Limited Edition','First Pressing','Reissue','Remastered','Deluxe Edition','Collector\'s Edition','Anniversary Edition','Promotional Copy','Import','Colored Vinyl','Picture Disc','Box Set','Not Specified'].includes(item.edition) ? item.edition : 'Standard Release';
            let tracksString = '';
            if (Array.isArray(item.tracks)) {
              tracksString = item.tracks
                .map(t => (typeof t === 'string' ? t : (t && t.title) ? `${t.position ? `${t.position}. ` : ''}${t.title}` : ''))
                .filter(Boolean)
                .join('\n');
            } else if (typeof item.tracks === 'string') {
              tracksString = item.tracks;
            }
            return {
              ...item,
              name: item.name || item.title,
              artist: item.artist,
              year: item.year,
              genre: item.genre,
              format: safeFormat,
              condition: safeCondition,
              edition: safeEdition,
              label: item.label,
              tracks: tracksString,
              imageUrl: item.imageUrl || ''
            };
          });
        }
      }

      // Add IDs to items that don't have them for React key props
      results = results.map((item, index) => {
        if (!item.id) {
          // Use different ID sources based on item type
          if (searchType === 'lego' && item.set_num) {
            return { ...item, id: item.set_num };
          } else if (searchType === 'consoles' && item.name) {
            // Use a display-only key; mark platformId=0 so the add route skips IGDB platform lookup
            return { ...item, id: `console-${item.name}-${index}`, platformId: item.platformId ?? 0 };
          } else {
            return { ...item, id: `item-${index}` };
          }
        }
        return item;
      });

      if (results.length > 0) {
        setSearchResults(results);
        setHasMoreResults(hasMore);
      } else {
        setError('No results found');
        setSearchResults([]);
      }
    } catch (error) {
      console.error(`Error searching ${searchType}:`, error);
      setError(error.response?.data?.error || `Failed to search ${searchType}`);
      setSearchResults([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Load more search results
  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMoreResults) return;

    try {
      setIsLoadingMore(true);
      const nextPage = searchPage + 1;
      const endpoint = getSearchEndpoint();
      const baseType = getBaseType();
      const response = await axiosInstance.get(endpoint, {
        params: { 
          query: searchQuery,
          page: nextPage,
          ...(baseType === 'games' && selectedPlatform ? { platform: selectedPlatform } : {}),
          _t: Date.now() // Add timestamp to prevent caching
        }
      });
      setSearchResults(prev => [...prev, ...response.data.results]);
      setSearchPage(nextPage);
      setHasMoreResults(!!response.data.next);
    } catch (error) {
      console.error(`Error loading more ${searchType}:`, error);
      setError(`Failed to load more ${searchType}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setSearchResults([]); // Clear results when typing
  };

  // Handle search on Enter key press
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      event.preventDefault();
      handleSearch(event);
    }
  };

  // Handle platform selection for an item
  const handlePlatformSelect = (itemId, platform) => {
    setSelectedItemPlatforms(prev => ({
      ...prev,
      [itemId]: platform
    }));
  };

  // Filter search results based on selected platform
  const getFilteredSearchResults = useCallback(() => {
    if (!selectedPlatform) return searchResults;

    const normalizePlatformName = (name) =>
      String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

    const selectedNorm = normalizePlatformName(selectedPlatform)
      .replace(/^ps5$/, 'playstation5')
      .replace(/^ps4$/, 'playstation4')
      .replace(/^ps3$/, 'playstation3')
      .replace(/^ps2$/, 'playstation2')
      .replace(/^ps1$/, 'playstation1');
    
    return searchResults.filter(item => {
      // Handle PC platform filtering in search results
      if (selectedPlatform === 'PC (Microsoft Windows)') {
        return item.platforms?.some(p => 
          p.platform.name === 'PC (Microsoft Windows)' || p.platform.name === 'PC'
        );
      }

      return item.platforms?.some(p => {
        const pName = p?.platform?.name;
        const pNorm = normalizePlatformName(pName)
          .replace(/^ps5$/, 'playstation5')
          .replace(/^ps4$/, 'playstation4')
          .replace(/^ps3$/, 'playstation3')
          .replace(/^ps2$/, 'playstation2')
          .replace(/^ps1$/, 'playstation1');
        return pNorm === selectedNorm;
      });
    });
  }, [searchResults, selectedPlatform]);

  // Clear all search and filter parameters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedPlatform('');
    setSearchResults([]);
  };

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    searchPage,
    hasMoreResults,
    isLoadingMore,
    isSearching,
    setIsSearching,
    loading,
    error,
    setError,
    selectedPlatform,
    setSelectedPlatform,
    selectedItemPlatforms,
    setSelectedItemPlatforms,
    handleSearch,
    handleLoadMore,
    handleSearchChange,
    handleSearchKeyPress,
    handlePlatformSelect,
    getFilteredSearchResults,
    handleClearFilters,
    searchSource,
    setSearchSource,
  };
};

export default useSearch;
